import { useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Search, X } from 'lucide-react';
import { MobileMenuAccordionSection } from './MobileMenuAccordionSection';
import { MENU_SECTIONS } from './mobileMenuConfig';

const COLORS = { navy: '#1B2A41', sage: '#8A9A86', rust: '#C25E4B' };
const STORAGE_KEY = 'cfr_last_menu_section';

export const menuRowStyle = { display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.6rem 0.75rem', borderRadius: '0.6rem', fontSize: '0.9rem', fontWeight: 600, color: COLORS.navy, textDecoration: 'none', cursor: 'pointer' };

const ALL_LINKS = MENU_SECTIONS.flatMap((section) => section.links.map((link) => ({ ...link, sectionTitle: section.title })));

/**
 * Renders the accordion sections (Courses for Addicts / Family, Churches, Support, About) shared
 * between SiteHeader.jsx and LandingNavbar.jsx, driven by mobileMenuConfig.js. Remembers the last
 * opened section (localStorage - survives full page reloads) so reopening the menu keeps it
 * expanded, and honours a section pre-selected by the quiz result (see QuizPage.jsx). A search box
 * up top lets returning members jump straight to any link without expanding sections.
 */
export const MobileMenuSections = ({ navigate, closeMenu, onRealStoriesClick }) => {
  const [openSection, setOpenSection] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [query, setQuery] = useState('');
  const toggleSection = (key) => {
    setOpenSection((prev) => {
      const next = prev === key ? null : key;
      try { localStorage.setItem(STORAGE_KEY, next || ''); } catch {}
      return next;
    });
  };

  const goToLink = (link) => {
    closeMenu();
    if (link.isRealStories) onRealStoriesClick();
    else navigate(link.path);
  };

  const trimmedQuery = query.trim().toLowerCase();
  const searchResults = trimmedQuery ? ALL_LINKS.filter((l) => l.label.toLowerCase().includes(trimmedQuery)) : [];

  return (
    <>
      <div style={{ position: 'relative', marginBottom: '0.5rem' }}>
        <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: COLORS.sage, pointerEvents: 'none' }} />
        <input
          data-testid="menu-search-input"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search for a course or page..."
          style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 2.25rem', borderRadius: '0.6rem', border: `1px solid ${COLORS.sage}55`, fontSize: '0.9rem', color: COLORS.navy, outline: 'none' }}
        />
        {query && (
          <button
            type="button"
            data-testid="menu-search-clear-btn"
            onClick={() => setQuery('')}
            style={{ position: 'absolute', right: '0.55rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: COLORS.sage, display: 'flex' }}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {trimmedQuery ? (
        <div data-testid="menu-search-results" style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
          {searchResults.length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: COLORS.sage, padding: '0.5rem 0.75rem', margin: 0 }}>No matches. Try a different word.</p>
          ) : searchResults.map((link) => {
            const Icon = link.icon;
            return (
              <motion.a
                key={link.path}
                data-testid={`menu-search-result-${link.testId || link.path}`}
                onClick={(e) => { e.preventDefault(); goToLink(link); }}
                href={link.path}
                whileTap={{ scale: 0.98 }}
                style={menuRowStyle}
              >
                <Icon size={16} style={{ flexShrink: 0 }} />
                <span>
                  {link.label}
                  <span style={{ display: 'block', fontWeight: 400, opacity: 0.55, fontSize: '0.75rem' }}>{link.sectionTitle}</span>
                </span>
              </motion.a>
            );
          })}
        </div>
      ) : (
        MENU_SECTIONS.map((section) => (
          <div key={section.key}>
            <MobileMenuAccordionSection
              title={section.title}
              isOpen={openSection === section.key}
              onToggle={() => toggleSection(section.key)}
              testId={section.testId}
            >
              {section.links.map((link) => {
                const Icon = link.icon;
                return (
                  <motion.a
                    key={link.path}
                    data-testid={link.testId}
                    onClick={(e) => { e.preventDefault(); goToLink(link); }}
                    href={link.path}
                    whileTap={{ scale: 0.98 }}
                    style={menuRowStyle}
                  >
                    <Icon size={16} style={{ flexShrink: 0 }} />
                    <span>
                      {link.label}
                      {link.sublabel && <span style={{ fontWeight: 400, opacity: 0.65 }}> · {link.sublabel}</span>}
                    </span>
                  </motion.a>
                );
              })}
            </MobileMenuAccordionSection>

            {section.key === 'family' && (
              <motion.a
                data-testid="menu-browse-all-courses-link"
                onClick={(e) => { e.preventDefault(); closeMenu(); navigate('/buy-courses'); }}
                href="/buy-courses"
                whileTap={{ scale: 0.98 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1rem',
                  background: `linear-gradient(135deg, ${COLORS.rust}15, ${COLORS.sage}10)`,
                  borderRadius: '0.75rem',
                  fontSize: '0.9375rem',
                  fontWeight: 700,
                  color: COLORS.rust,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  border: `2px solid ${COLORS.rust}`,
                  marginTop: '0.25rem'
                }}
              >
                <BookOpen size={18} />
                <span>See All Courses &amp; Pricing</span>
              </motion.a>
            )}
          </div>
        ))
      )}
    </>
  );
};

export default MobileMenuSections;
